import { showToast, showSaved, showError } from '../ui/GlobalToast';
import { GuideBanner }       from '../ui/GuideBanner';
import { HelpTip }           from '../ui/HelpTip';
import { FieldError, inputStyle } from '../ui/useFormValidation';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, Clock, MapPin, Plus, Edit2, Trash2, Save, X, Star } from 'lucide-react';

interface ProgrammeEvent {
  id: string;
  title: string;
  time: string;
  endTime: string;
  location: string;
  description: string;
  type: string;
  speaker: string;
  isHighlight: boolean;
  sortOrder?: number;
}

interface ProgrammeDay {
  id: string;
  date: string;
  dayLabel: string;
  theme: string;
  events: ProgrammeEvent[];
}

const EVENT_TYPES = [
  { value: 'ceremony',    label: 'Ceremony',     color: '#C9A84C' },
  { value: 'cultural',    label: 'Cultural',     color: '#8B5CF6' },
  { value: 'sports',      label: 'Sports',       color: '#10B981' },
  { value: 'religious',   label: 'Religious',    color: '#F59E0B' },
  { value: 'community',   label: 'Community',    color: '#3B82F6' },
  { value: 'diaspora',    label: 'Diaspora',     color: '#EC4899' },
  { value: 'award',       label: 'Award',        color: '#EF4444' },
  { value: 'competition', label: 'Competition',  color: '#F97316' },
  { value: 'general',     label: 'General',      color: '#6B7280' },
];

const EMPTY_EVENT: Omit<ProgrammeEvent,'id'> = {
  title:'', time:'', endTime:'', location:'', description:'',
  type:'general', speaker:'', isHighlight:false
};

function Toast({ msg, ok }: { msg:string; ok:boolean }) {
  return (
    <div style={{position:'fixed',bottom:24,right:24,zIndex:9999,
      background: ok ? '#0F5132' : '#7f1d1d',
      border: ok ? '1px solid #C9A84C' : '1px solid #ef4444',
      borderRadius:10,padding:'12px 20px',
      color: ok ? '#C9A84C' : '#ef4444',
      fontWeight:600,fontSize:14,boxShadow:'0 4px 24px rgba(0,0,0,0.5)'}}>
      {msg}
    </div>
  );
}

export default function FestivalProgrammeBuilder() {
  const [days, setDays] = useState<ProgrammeDay[]>([]);
  const [activeDay, setActiveDay] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editEvt, setEditEvt] = useState<ProgrammeEvent|null>(null);
  const [editDayId, setEditDayId] = useState('');
  const [form, setForm] = useState<Omit<ProgrammeEvent,'id'>>({...EMPTY_EVENT});
  const [toastMsg, setToastMsg] = useState('');
  const [toastOk, setToastOk] = useState(true);
  const [toastVisible, setToastVisible] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{dayId:string;eventId:string}|null>(null);
  const autoSaveRef = useRef<ReturnType<typeof setTimeout>|null>(null);

  const toast = (msg: string, ok = true) => {
    setToastMsg(msg); setToastOk(ok); setToastVisible(true);
    setTimeout(() => setToastVisible(false), 3500);
  };

  const getToken = () =>
    localStorage.getItem('erue_auth_token') ||
    sessionStorage.getItem('erue_auth_token') || '';

  const loadProgramme = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch('/api/programme');
      const d = await r.json();
      const arr: ProgrammeDay[] = Array.isArray(d) ? d
        : Array.isArray(d.days) ? d.days
        : Array.isArray(d.programme) ? d.programme : [];
      arr.forEach((day: ProgrammeDay) => {
        if (!Array.isArray(day.events)) day.events = [];
      });
      setDays(arr);
    } catch { setError('Failed to load programme. Please refresh.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProgramme(); }, [loadProgramme]);

  const saveProgramme = useCallback(async (data: ProgrammeDay[], silent = false) => {
    const tok = getToken();
    if (!tok) { if (!silent) showError('Please log in first — click the Login button in the navigation bar.'); return; }
    setSaving(true);
    try {
      const payload = data.map((day: ProgrammeDay) => ({
        id: day.id,
        date: day.date || '',
        dayLabel: day.dayLabel || '',
        theme: day.theme || '',
        events: (day.events || []).map((e: ProgrammeEvent, i: number) => ({
          id: e.id,
          title: e.title || 'Untitled Event',
          time: e.time || '',
          endTime: e.endTime || '',
          location: e.location || '',
          description: e.description || '',
          type: e.type || 'general',
          speaker: e.speaker || '',
          isHighlight: e.isHighlight || false,
          sortOrder: i,
        }))
      }));
      const r = await fetch('/api/programme', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + tok },
        body: JSON.stringify({ days: payload })
      });
      const result = await r.json();
      if (!r.ok) throw new Error(result?.error || 'Save failed');
      if (!silent) showSaved('Programme saved — ' + (result.totalEvents ?? 0) + ' event' + ((result.totalEvents ?? 0) !== 1 ? 's' : '') + ' across ' + result.saved + ' days');
    } catch(e: any) {
      showError('Save failed: ' + (e.message || 'Check your connection and try again'));
      console.error('[SAVE]', e);
    } finally { setSaving(false); }
  }, []);

  const scheduleSave = (data: ProgrammeDay[]) => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => { showToast('Auto-saving changes...', 'saving', 99999); saveProgramme(data, true); }, 800);
  };

  const openAdd = (dayId: string) => {
    setEditEvt(null); setEditDayId(dayId);
    setForm({...EMPTY_EVENT}); setShowModal(true);
  };

  const openEdit = (dayId: string, evt: ProgrammeEvent) => {
    setEditEvt(evt); setEditDayId(dayId);
    setForm({
      title: evt.title, time: evt.time, endTime: evt.endTime,
      location: evt.location, description: evt.description,
      type: evt.type, speaker: evt.speaker, isHighlight: evt.isHighlight
    });
    setShowModal(true);
  };

  const saveEvent = () => {
      <GuideBanner
        id="programme-builder"
        type="tip"
        icon="📋"
        title="Building the Festival Programme"
        message="Add events for each day of the festival (December 24–30, 2026). Fill in the event title, time, venue and type, then click Save Programme. Your changes are saved automatically to the database and will appear live on the website."
      />
    const updated = days.map((day: ProgrammeDay) => {
      if (day.id !== editDayId) return day;
      let events: ProgrammeEvent[];
      if (editEvt) {
        events = day.events.map((e: ProgrammeEvent) =>
          e.id === editEvt.id ? { ...e, ...form } : e
        );
      } else {
        const neo: ProgrammeEvent = {
          id: 'evt_' + Date.now() + '_' + Math.random().toString(36).substr(2,4),
          ...form
        };
        events = [...(day.events || []), neo];
      }
      return { ...day, events };
    });
    setDays(updated);
    scheduleSave(updated);
    setShowModal(false);
  };

  const doDelete = () => {
    if (!deleteConfirm) return;
    const updated = days.map((day: ProgrammeDay) =>
      day.id !== deleteConfirm.dayId ? day
        : { ...day, events: day.events.filter((e: ProgrammeEvent) => e.id !== deleteConfirm.eventId) }
    );
    setDays(updated);
    scheduleSave(updated);
    setDeleteConfirm(null);
  };

  const currentDay = days[activeDay];
  const totalEvents = days.reduce((s: number, d: ProgrammeDay) => s + (d.events?.length || 0), 0);
  const getTypeInfo = (type: string) => EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[8];

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',
      minHeight:'60vh',color:'rgba(237,232,220,0.5)',fontSize:16}}>
      Loading programme...
    </div>
  );

  if (error) return (
    <div style={{padding:32,textAlign:'center'}}>
      <p style={{color:'#ef4444',marginBottom:16}}>{error}</p>
      <button onClick={loadProgramme}
        style={{padding:'10px 24px',background:'#0F5132',border:'1px solid #C9A84C',
          borderRadius:8,color:'#C9A84C',cursor:'pointer',fontWeight:700}}>
        Retry
      </button>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#070D0A',color:'#EDE8DC',paddingBottom:80}}>
      {/* Toast handled globally by GlobalToastContainer */}

      <div style={{background:'#0E1812',borderBottom:'1px solid #1C2E22',padding:'16px 24px'}}>
        <div style={{maxWidth:900,margin:'0 auto',display:'flex',
          alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:'#EDE8DC',
              fontFamily:"'Cinzel',serif",margin:0}}>Festival Programme Builder</h1>
            <p style={{color:'rgba(237,232,220,0.5)',fontSize:13,margin:'4px 0 0'}}>
              ERUE Festival — December 24-30, 2026 | {totalEvents} events across {days.length} days
            </p>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={loadProgramme}
              style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',
                background:'#0E1812',border:'1px solid #1C2E22',borderRadius:8,
                color:'rgba(237,232,220,0.7)',fontSize:13,cursor:'pointer'}}>
              Reload
            </button>
            <button onClick={() => saveProgramme(days)} disabled={saving}
              style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',
                background:saving ? '#1C2E22' : '#0F5132',
                border:'1px solid #C9A84C',borderRadius:8,color:'#C9A84C',
                fontSize:13,cursor:saving ? 'not-allowed' : 'pointer',fontWeight:700}}>
              <Save size={14}/> {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div style={{background:'#0E1812',borderBottom:'1px solid #1C2E22',overflowX:'auto'}}>
        <div style={{maxWidth:900,margin:'0 auto',display:'flex',gap:4,padding:'8px 24px'}}>
          {days.map((day: ProgrammeDay, i: number) => (
            <button key={day.id} onClick={() => setActiveDay(i)}
              style={{padding:'8px 16px',borderRadius:8,border:'none',cursor:'pointer',
                fontWeight:i===activeDay ? 700 : 400,fontSize:13,whiteSpace:'nowrap',
                background: i===activeDay ? '#C9A84C' : 'transparent',
                color: i===activeDay ? '#070D0A' : 'rgba(237,232,220,0.6)'}}>
              {day.dayLabel ? day.dayLabel.split('—')[0].trim() : 'Day ' + (i+1)}
            </button>
          ))}
        </div>
      </div>

      {currentDay && (
        <div style={{maxWidth:900,margin:'0 auto',padding:'24px 16px'}}>
          <div style={{marginBottom:24,padding:'16px 20px',
            background:'#0E1812',border:'1px solid #1C2E22',borderRadius:12}}>
            <h2 style={{fontSize:18,fontWeight:700,color:'#C9A84C',margin:'0 0 4px'}}>
              {currentDay.dayLabel}
            </h2>
            <p style={{color:'rgba(237,232,220,0.6)',margin:0,fontSize:14}}>
              {currentDay.date} — {currentDay.theme}
            </p>
            <p style={{color:'rgba(237,232,220,0.4)',margin:'4px 0 0',fontSize:13}}>
              {(currentDay.events||[]).length} event{(currentDay.events||[]).length !== 1 ? 's' : ''} scheduled
            </p>
          </div>

          {(currentDay.events||[]).length === 0 ? (
            <div style={{textAlign:'center',padding:'48px 24px',
              background:'#0E1812',border:'1px dashed #1C2E22',borderRadius:12}}>
              <Calendar size={40} style={{margin:'0 auto 16px',color:'rgba(237,232,220,0.2)'}} />
              <p style={{color:'rgba(237,232,220,0.4)',marginBottom:20}}>
                No events scheduled for this day
              </p>
              <button onClick={() => openAdd(currentDay.id)}
                style={{padding:'10px 24px',background:'#C9A84C',border:'none',
                  borderRadius:8,color:'#070D0A',fontWeight:700,cursor:'pointer',fontSize:14}}>
                + Add First Event
              </button>
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              {(currentDay.events||[]).map((evt: ProgrammeEvent) => {
                const typeInfo = getTypeInfo(evt.type);
                return (
                  <div key={evt.id}
                    style={{background:'#0E1812',border:'1px solid #1C2E22',
                      borderLeft:'3px solid ' + typeInfo.color,
                      borderRadius:10,padding:'14px 16px'}}>
                    <div style={{display:'flex',alignItems:'flex-start',
                      justifyContent:'space-between',gap:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,
                          marginBottom:6,flexWrap:'wrap'}}>
                          {evt.isHighlight && (
                            <Star size={12} style={{color:'#C9A84C',fill:'#C9A84C'}} />
                          )}
                          <span style={{fontSize:15,fontWeight:700,color:'#EDE8DC'}}>
                            {evt.title}
                          </span>
                          <span style={{padding:'2px 8px',borderRadius:10,fontSize:11,
                            fontWeight:600,background:typeInfo.color+'22',
                            color:typeInfo.color,border:'1px solid '+typeInfo.color+'44'}}>
                            {typeInfo.label}
                          </span>
                        </div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:'8px 16px',
                          fontSize:12,color:'rgba(237,232,220,0.5)'}}>
                          {evt.time && (
                            <span style={{display:'flex',alignItems:'center',gap:4}}>
                              <Clock size={11}/> {evt.time}{evt.endTime ? ' - ' + evt.endTime : ''}
                            </span>
                          )}
                          {evt.location && (
                            <span style={{display:'flex',alignItems:'center',gap:4}}>
                              <MapPin size={11}/> {evt.location}
                            </span>
                          )}
                          {evt.speaker && (
                            <span>Speaker: {evt.speaker}</span>
                          )}
                        </div>
                        {evt.description && (
                          <p style={{fontSize:13,color:'rgba(237,232,220,0.5)',
                            margin:'8px 0 0',lineHeight:1.5}}>{evt.description}</p>
                        )}
                      </div>
                      <div style={{display:'flex',gap:6,flexShrink:0}}>
                        <button onClick={() => openEdit(currentDay.id, evt)}
                          style={{padding:'5px 10px',background:'transparent',
                            border:'1px solid #1C2E22',borderRadius:6,color:'#60a5fa',
                            cursor:'pointer',fontSize:12,display:'flex',
                            alignItems:'center',gap:4}}>
                          <Edit2 size={11}/> Edit
                        </button>
                        <button onClick={() => setDeleteConfirm({dayId:currentDay.id,eventId:evt.id})}
                          style={{padding:'5px 10px',background:'transparent',
                            border:'1px solid #1C2E22',borderRadius:6,color:'#ef4444',
                            cursor:'pointer',fontSize:12,display:'flex',
                            alignItems:'center',gap:4}}>
                          <Trash2 size={11}/> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <button onClick={() => openAdd(currentDay.id)}
                style={{marginTop:8,padding:'10px 24px',background:'#C9A84C',
                  border:'none',borderRadius:8,color:'#070D0A',
                  fontWeight:700,cursor:'pointer',fontSize:14}}>
                + Add Event
              </button>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:1000,
          display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
          onClick={() => setShowModal(false)}>
          <div style={{background:'#0E1812',border:'1px solid #C9A84C',borderRadius:16,
            padding:28,width:'100%',maxWidth:560,maxHeight:'90vh',overflowY:'auto'}}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',
              alignItems:'center',marginBottom:24}}>
              <h3 style={{margin:0,fontSize:18,fontWeight:700,color:'#C9A84C'}}>
                {editEvt ? 'Edit Event' : 'Add Event'}
              </h3>
              <button onClick={() => setShowModal(false)}
                style={{background:'none',border:'none',
                  color:'rgba(237,232,220,0.5)',cursor:'pointer'}}>
                <X size={20}/>
              </button>
            </div>

            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              <div>
                <label style={{fontSize:12,color:'#B8C4BB',display:'block',marginBottom:6}}>
                  Event Title *
                </label>
                <input value={form.title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm(f => ({...f, title: e.target.value}))}
                  placeholder="e.g. Grand Opening Ceremony"
                  style={{width:'100%',background:'#070D0A',border:'1px solid #1C2E22',
                    borderRadius:8,padding:'10px 12px',color:'#EDE8DC',fontSize:14,
                    boxSizing:'border-box',outline:'none'}} />
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div>
                  <label style={{fontSize:12,color:'#B8C4BB',display:'block',marginBottom:6}}>
                    Start Time
                  </label>
                  <input type="time" value={form.time}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setForm(f => ({...f, time: e.target.value}))}
                    style={{width:'100%',background:'#070D0A',border:'1px solid #1C2E22',
                      borderRadius:8,padding:'10px 12px',color:'#EDE8DC',fontSize:14,
                      boxSizing:'border-box',outline:'none'}} />
                </div>
                <div>
                  <label style={{fontSize:12,color:'#B8C4BB',display:'block',marginBottom:6}}>
                    End Time
                  </label>
                  <input type="time" value={form.endTime}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setForm(f => ({...f, endTime: e.target.value}))}
                    style={{width:'100%',background:'#070D0A',border:'1px solid #1C2E22',
                      borderRadius:8,padding:'10px 12px',color:'#EDE8DC',fontSize:14,
                      boxSizing:'border-box',outline:'none'}} />
                </div>
              </div>

              <div>
                <label style={{fontSize:12,color:'#B8C4BB',display:'block',marginBottom:6}}>
                  Location / Venue
                </label>
                <input value={form.location}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm(f => ({...f, location: e.target.value}))}
                  placeholder="e.g. Palace Square, Uzairue"
                  style={{width:'100%',background:'#070D0A',border:'1px solid #1C2E22',
                    borderRadius:8,padding:'10px 12px',color:'#EDE8DC',fontSize:14,
                    boxSizing:'border-box',outline:'none'}} />
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div>
                  <label style={{fontSize:12,color:'#B8C4BB',display:'block',marginBottom:6}}>
                    Event Type
                  </label>
                  <select value={form.type}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      setForm(f => ({...f, type: e.target.value}))}
                    style={{width:'100%',background:'#070D0A',border:'1px solid #1C2E22',
                      borderRadius:8,padding:'10px 12px',color:'#EDE8DC',fontSize:14,
                      boxSizing:'border-box',outline:'none'}}>
                    {EVENT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{fontSize:12,color:'#B8C4BB',display:'block',marginBottom:6}}>
                    Speaker / Guest
                  </label>
                  <input value={form.speaker}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setForm(f => ({...f, speaker: e.target.value}))}
                    placeholder="Speaker name"
                    style={{width:'100%',background:'#070D0A',border:'1px solid #1C2E22',
                      borderRadius:8,padding:'10px 12px',color:'#EDE8DC',fontSize:14,
                      boxSizing:'border-box',outline:'none'}} />
                </div>
              </div>

              <div>
                <label style={{fontSize:12,color:'#B8C4BB',display:'block',marginBottom:6}}>
                  Description
                </label>
                <textarea value={form.description} rows={3}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setForm(f => ({...f, description: e.target.value}))}
                  placeholder="Brief description of the event..."
                  style={{width:'100%',background:'#070D0A',border:'1px solid #1C2E22',
                    borderRadius:8,padding:'10px 12px',color:'#EDE8DC',fontSize:14,
                    boxSizing:'border-box',outline:'none',resize:'vertical',
                    fontFamily:'inherit'}} />
              </div>

              <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
                <input type="checkbox" checked={form.isHighlight}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm(f => ({...f, isHighlight: e.target.checked}))}
                  style={{width:16,height:16,cursor:'pointer'}} />
                <span style={{fontSize:14,color:'#C9A84C',fontWeight:600}}>
                  Mark as Highlight Event
                </span>
              </label>

              <div style={{display:'flex',gap:12,marginTop:8}}>
                <button onClick={() => setShowModal(false)}
                  style={{flex:1,padding:'11px 0',background:'transparent',
                    border:'1px solid #1C2E22',borderRadius:8,
                    color:'rgba(237,232,220,0.7)',cursor:'pointer',fontSize:14}}>
                  Cancel
                </button>
                <button onClick={saveEvent}
                  style={{flex:2,padding:'11px 0',background:'#C9A84C',
                    border:'none',borderRadius:8,color:'#070D0A',
                    fontWeight:700,cursor:'pointer',fontSize:14}}>
                  {editEvt ? 'Update Event' : 'Add Event'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:1000,
          display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#0E1812',border:'1px solid #ef4444',borderRadius:16,
            padding:28,width:'100%',maxWidth:400,textAlign:'center'}}>
            <Trash2 size={32} style={{color:'#ef4444',margin:'0 auto 16px'}} />
            <h3 style={{color:'#EDE8DC',margin:'0 0 8px',fontSize:18}}>Delete Event?</h3>
            <p style={{color:'rgba(237,232,220,0.5)',marginBottom:24,fontSize:14}}>
              This cannot be undone.
            </p>
            <div style={{display:'flex',gap:12}}>
              <button onClick={() => setDeleteConfirm(null)}
                style={{flex:1,padding:'10px 0',background:'transparent',
                  border:'1px solid #1C2E22',borderRadius:8,
                  color:'rgba(237,232,220,0.7)',cursor:'pointer'}}>
                Cancel
              </button>
              <button onClick={doDelete}
                style={{flex:1,padding:'10px 0',background:'#7f1d1d',
                  border:'1px solid #ef4444',borderRadius:8,
                  color:'#ef4444',fontWeight:700,cursor:'pointer'}}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
